/*
 * File: CourseFeeInput.tsx
 * Author: Austin Robinson
 * Date Created: 10/1/2023
 * Copyright (c) DrivingDoc 2023
 */
import Box from '@mui/material/Box';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';

import CurrencyInput from 'components/Forms/CurrencyInput.tsx';

type TextFieldPropsReqName = Required<Pick<TextFieldProps, 'name'>> & Omit<TextFieldProps, 'name'>;

interface CourseFeeInputProps {
  amountFieldName: string;
  label?: string;
  numAllowedProps: TextFieldPropsReqName;
  dueProps: TextFieldPropsReqName;
  lockWhenOverdueProps: CheckboxProps;
}

export default function CourseFeeInput({
  amountFieldName,
  label,
  numAllowedProps,
  dueProps,
  lockWhenOverdueProps,
}: CourseFeeInputProps) {
  const {
    formState: { errors },
  } = useFormContext();

  const numAllowedHelperText = errors[numAllowedProps.name]?.message;
  const daysUntilDueHelperText = errors[dueProps.name]?.message;
  return (
    <Stack>
      <Stack direction="row" gap={2}>
        <CurrencyInput name={amountFieldName} label={label ?? 'Amount'} textFieldProps={{ fullWidth: false }} />
        <TextField
          label="# Allowed"
          variant="standard"
          type="number"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          error={!!errors[numAllowedProps.name]}
          helperText={typeof numAllowedHelperText === 'string' ? numAllowedHelperText : undefined}
          {...numAllowedProps}
        />
        <TextField
          label="Days Until Due"
          variant="standard"
          type="number"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          error={!!errors[dueProps.name]}
          helperText={typeof daysUntilDueHelperText === 'string' ? daysUntilDueHelperText : undefined}
          {...dueProps}
        />
        <Box display="flex" alignItems="center">
          <Box>
            <Checkbox {...lockWhenOverdueProps} id={lockWhenOverdueProps.name} />
          </Box>
          <Box component="label" htmlFor={lockWhenOverdueProps.name}>
            <Typography fontSize={16}>Lock Account</Typography>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
}
